import React, { useState } from "react";
import { DialogLayout } from "subframe/layouts/DialogLayout";
import { Select } from "subframe/components/Select";
import { TextArea } from "subframe/components/TextArea";
import { Button } from "subframe/components/Button";
import * as SubframeCore from "@subframe/core";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useDebounce } from "hooks/useDebounce";
import { fetchCaregiverRolesByAgency } from "services/retention/employees";
import { Caregiver } from "modules/Retention/types/taskCenter";
import { EscalationPayload } from "modules/Retention/types";
import endpoints from "constants/retention/endpoints";
import { get } from "utils/localStorage";
import { followUpEscalation } from "modules/Retention/services/retention";
import { notify } from "modules/Retention/providers/NotificationProvider";
import { AlertVariant } from "modules/Retention/types";
import useUser from "hooks/userHooks";

interface ForwardEscalationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit?: (data: ForwardEscalationData) => void;
  escalationId?: number;
  employeeId?: number;
}

interface ForwardEscalationData {
  forwardTo: Caregiver;
  additionalContext: string;
}

export const ForwardEscalationDialog: React.FC<ForwardEscalationDialogProps> = ({
  open,
  onOpenChange,
  onSubmit,
  escalationId,
  employeeId,
}) => {
  const [forwardTo, setForwardTo] = useState<Caregiver | null>(null);
  const [additionalContext, setAdditionalContext] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Get user info
  const { user } = useUser();
  const userFromStorage = get("user") as any;
  const agencyId = user?.agency_id || userFromStorage?.agency_id || 16;

  // Fetch caregivers with debounced search
  const {
    data: caregiversData,
    isLoading: isLoadingCaregivers,
  } = useQuery({
    queryKey: [endpoints.caregiverRolesByAgency, agencyId, debouncedSearchQuery],
    queryFn: () => {
      const params: any = {
        agencyId,
        page: 1,
        limit: 1000,
      };
      
      if (debouncedSearchQuery) {
        params.filters = JSON.stringify([{ field: "employeeName", value: debouncedSearchQuery }]);
      }
      
      return fetchCaregiverRolesByAgency(params);
    },
    select: (res: any) => res?.data || res,
    enabled: open,
    staleTime: 180000,
  });

  const caregivers = caregiversData?.result?.caregiver_roles || [];

  // Forward escalation mutation
  const forwardMutation = useMutation({
    mutationFn: async (payload: EscalationPayload) => {
      return await followUpEscalation(payload);
    },
    onSuccess: () => {
      notify({
        title: "Escalation forwarded successfully",
        description: `Escalation has been forwarded to ${forwardTo?.first_name} ${forwardTo?.last_name}`,
        variant: AlertVariant.SUCCESS
      });
      if (onSubmit && forwardTo) {
        onSubmit({
          forwardTo,
          additionalContext,
        });
      }
      handleClose();
    },
    onError: (error: any) => {
      console.error("Forward escalation error:", error);
      notify({
        title: "Failed to forward escalation",
        description: error?.message || "An error occurred while forwarding the escalation. Please try again.",
        variant: AlertVariant.ERROR
      });
    },
  });

  const handleSubmit = () => {
    if (!forwardTo || !escalationId || !employeeId) {
      notify({
        title: "Missing required information",
        description: "Please select a caregiver to forward to.",
        variant: AlertVariant.ERROR
      });
      return;
    }

    const payload: EscalationPayload = {
      escalation_id: escalationId,
      employee_id: employeeId,
      outcome: "NEEDS_ATTENTION",
      notes: additionalContext || "Escalation forwarded for review",
      should_forward: true,
      forward_email: forwardTo.email,
      noter_email: user?.email || userFromStorage?.email || "",
      tags: ["Escalation Forwarded"],
      note_tags: []
    };

    forwardMutation.mutate(payload);
  };

  const handleClose = () => {
    setForwardTo(null);
    setAdditionalContext("");
    setSearchQuery("");
    onOpenChange(false);
  };

  return (
    <div className="z-30">
      <DialogLayout open={open} onOpenChange={onOpenChange}>
        <div className="flex h-full w-112 flex-col items-start gap-6 rounded-lg bg-default-background px-6 py-6">
        <div className="flex w-full flex-col items-start gap-6">
          <div className="flex items-center gap-2">
            <SubframeCore.Icon
              className="text-section-header font-section-header text-default-font"
              name="FeatherSend"
            />
            <span className="text-section-header font-section-header text-default-font">
              Forward Escalation
            </span>
          </div>
          <div className="flex w-full flex-col items-start gap-4">
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-body-bold font-body-bold text-default-font">
                Forward to*
              </span>
              <Select
                className="h-auto w-full flex-none"
                label=""
                placeholder="Search by name..."
                helpText=""
                value={forwardTo?.caregiver_id?.toString() || ""}
                onValueChange={(value: string) => {
                  if (value === "loading" || value === "no-results") return;
                  const selectedCaregiver = caregivers.find((c: any) => c.caregiver_id.toString() === value);
                  setForwardTo(selectedCaregiver || null);
                }}
              >
                {isLoadingCaregivers ? (
                  <Select.Item value="loading" disabled>Loading caregivers...</Select.Item>
                ) : caregivers.length === 0 ? (
                  <Select.Item value="no-results" disabled>No caregivers found</Select.Item>
                ) : (
                  caregivers.map((caregiver: any) => (
                    <Select.Item key={caregiver.caregiver_id} value={caregiver.caregiver_id.toString()}>
                      {caregiver.first_name} {caregiver.last_name}
                    </Select.Item>
                  ))
                )}
              </Select>
            </div>
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-body-bold font-body-bold text-default-font">
                Additional Context
              </span>
              <TextArea className="h-auto w-full flex-none" label="" helpText="">
                <TextArea.Input
                  placeholder="Add a note explaining why you're forwarding this escalation or what action is needed."
                  value={additionalContext}
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setAdditionalContext(event.target.value)
                  }
                />
              </TextArea>
            </div>
          </div>
        </div>
        <div className="flex w-full items-start gap-6">
          <Button
            className="h-8 grow shrink-0 basis-0"
            onClick={handleSubmit}
            disabled={!forwardTo || isLoadingCaregivers || forwardMutation.isPending}
            loading={forwardMutation.isPending}
          >
            Send Forward
          </Button>
          <Button
            className="h-8 grow shrink-0 basis-0"
            variant="neutral-secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>
        </div>
      </div>
    </DialogLayout>
    </div>
  );
}; 